import { flushSync } from 'react-dom'

type DocumentWithViewTransition = Document & {
  startViewTransition?: (update: () => void) => { ready: Promise<void> }
}

const REVEAL_DURATION_MS = 300

/**
 * Change de thème en faisant monter le nouveau du bas vers le haut de la fenêtre (View Transitions API).
 * Sans l'API, ou en mouvement réduit, le changement est immédiat.
 * Les pseudo-éléments `::view-transition-*(root)` perdent leur fondu par défaut dans `MuiCssBaseline`.
 */
export function revealThemeChange<T>(apply: () => T): Promise<T> {
  const doc = document as DocumentWithViewTransition
  if (
    !doc.startViewTransition ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return Promise.resolve(apply())
  }

  return new Promise<T>((resolve) => {
    const transition = doc.startViewTransition!(() => {
      let result!: T
      // Le nouvel écran doit être rendu avant que le navigateur le capture.
      flushSync(() => {
        result = apply()
      })
      resolve(result)
    })
    transition.ready
      .then(() => {
        document.documentElement.animate(
          { clipPath: ['inset(100% 0 0 0)', 'inset(0 0 0 0)'] },
          {
            duration: REVEAL_DURATION_MS,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            pseudoElement: '::view-transition-new(root)',
          }
        )
      })
      .catch(() => undefined)
  })
}
